import { z } from 'zod';
import { dataHoraSchema } from './comum.js';
/**
 * Os SEIS cargos.
 *
 * Antes existiam tres listas discordantes — `supabase_security.sql` com quatro,
 * `server.ts` com seis, `hierarchy_and_rls.sql` com quatro outros — e criar um CEO
 * criava usuario orfao no Auth e devolvia 500 (BUG-1). Agora a lista existe uma vez
 * so, aqui, e tanto o banco (enum do Prisma) quanto a API quanto os paineis derivam
 * dela. Divergencia volta a ser erro de compilacao.
 */
export const cargoSchema = z.enum(['admin', 'ceo', 'gestora', 'diretora', 'lider', 'vendedor']);
/** Hierarquia: quem enxerga quem. Indice = nivel; menor numero enxerga mais. */
export const NIVEL_CARGO = {
    admin: 0,
    ceo: 1,
    gestora: 2,
    diretora: 3,
    lider: 4,
    vendedor: 5,
};
export function cargoAlcanca(deQuem, alvo) {
    return NIVEL_CARGO[deQuem] <= NIVEL_CARGO[alvo];
}
/** Usuario interno da Nyroh (equipe). Autenticado pelo Better Auth. */
export const usuarioSchema = z.object({
    id: z.string(),
    nome: z.string(),
    email: z.email(),
    emailVerificado: z.boolean(),
    imagem: z.string().nullable(),
    cargo: cargoSchema,
    /** Superior direto. `null` no topo da hierarquia. */
    gestorId: z.string().nullable(),
    ativo: z.boolean(),
    criadoEm: dataHoraSchema,
});
/**
 * A regra unica de senha de OPERADOR.
 *
 * Vive aqui, e nao dentro de `criarUsuarioSchema`, pelo mesmo motivo de
 * `senhaPortalSchema` logo abaixo: ela e escrita por mais de um lugar. O painel
 * comercial ganhou a propria rota de cadastro de vendedor
 * (`comercial.lideranca.criarVendedor`) e, se cada schema repetisse o `min(12)`,
 * bastaria um deles ficar para tras para existir um caminho oficial de criar
 * operador com senha curta. A tela anterior daquele painel tinha exatamente
 * isso: uma senha padrao de poucos caracteres escrita no proprio componente.
 *
 * O maximo e 72 porque e o limite do argon2 — sem ele, uma senha mais longa
 * seria truncada silenciosamente na hora do hash e o login seguinte funcionaria
 * com um prefixo, o que e pior que recusar.
 */
export const senhaOperadorSchema = z
    .string()
    .min(12, 'senha de operador tem no minimo 12 caracteres')
    .max(72, 'a senha passa de 72 caracteres');
export const criarUsuarioSchema = z.object({
    nome: z.string().min(2),
    email: z.email(),
    senha: senhaOperadorSchema,
    cargo: cargoSchema,
    gestorId: z.string().nullable().optional(),
});
export const atualizarUsuarioSchema = criarUsuarioSchema
    .omit({ senha: true })
    .partial()
    .extend({ ativo: z.boolean().optional() });
/**
 * Login do CLIENTE final no painel do proprio site (username + PIN).
 *
 * Continua existindo porque e uma decisao comercial, nao tecnica. O que muda:
 * o PIN nunca mais trafega como filtro de query nem e comparado no navegador.
 * Vai por POST, e comparado contra argon2id no servidor, e tem limite de tentativa
 * por conta e por IP. Ver `docs/nyroh-diagnostico-e-arquitetura.html`, secao 16.
 */
export const loginClienteSchema = z.object({
    usuario: z.string().min(1).max(80),
    /**
     * O campo se chama `pin` por historia, mas aceita senha com letras.
     *
     * O minimo continua 4 para nao expulsar quem ainda tem PIN de 4 digitos
     * emitido antes; o maximo subiu de 12 para 72 (o limite do argon2) porque
     * senha e melhor que PIN e nada aqui deve impedir uma boa.
     */
    pin: z.string().min(4).max(72),
});
export const sessaoClienteSchema = z.object({
    token: z.string(),
    expiraEm: dataHoraSchema,
    clienteId: z.uuid(),
    slug: z.string(),
    nome: z.string(),
    /**
     * Verdadeiro enquanto a senha for a padrao entregue pelo operador.
     *
     * O painel usa isto para levar a pessoa direto para a troca de senha. Nao e
     * decoracao: uma senha padrao que ninguem troca e uma senha publicada.
     */
    precisaTrocarSenha: z.boolean().default(false),
});
/**
 * O usuario de portal derivado do slug do cliente.
 *
 * Vive no CONTRATO porque servidor e painel precisam concordar. O painel gerava
 * `slug.replace(/-/g,'').substring(0,15)` no navegador e gravava o resultado num
 * lugar que o login nao lia; o mapeador, por sua vez, mostrava o slug CRU como
 * "usuario". Tres regras diferentes para o mesmo campo — e uma conta que existe
 * e cujo dono nao consegue adivinhar como entrar.
 */
export function usuarioPadraoDoSlug(slug) {
    const base = slug.toLowerCase().replace(/[^a-z0-9]/g, '');
    // `usuario` e UNIQUE no banco e o schema exige 3 caracteres. Slug curto demais
    // e raro, mas quando acontece a conta simplesmente nao seria criada — entao
    // completa, em vez de falhar.
    return (base.length >= 3 ? base : `${base}cliente`).slice(0, 80);
}
/**
 * Regra unica de senha do portal, usada nos tres lugares que a escrevem:
 * a troca pelo proprio cliente, a redefinicao pelo operador e o script de
 * senha padrao. Escrita uma vez para nao divergir entre eles.
 */
export const senhaPortalSchema = z
    .string()
    .min(8, 'a senha precisa de pelo menos 8 caracteres')
    .max(72, 'a senha passa de 72 caracteres');
/** O cliente trocando a propria senha. Exige a atual: sessao roubada nao troca. */
export const trocarSenhaPortalSchema = z.object({
    senhaAtual: z.string().min(1).max(72),
    novaSenha: senhaPortalSchema,
});
/**
 * O que o painel do operador pode saber sobre a credencial de um cliente.
 *
 * Nao ha campo de senha aqui, e e proposital: o que fica gravado e o hash
 * argon2id, e nao existe consulta que o desfaca. O painel mostra SE ha acesso,
 * qual o usuario e quando foi usado — nunca o segredo.
 */
export const credencialClienteSchema = z.object({
    temAcesso: z.boolean(),
    usuario: z.string().nullable(),
    ultimoAcesso: dataHoraSchema.nullable(),
    precisaTrocarSenha: z.boolean(),
    bloqueadoAte: dataHoraSchema.nullable(),
});
/** Operador definindo ou redefinindo a credencial de um cliente. */
export const definirCredencialClienteSchema = z.object({
    /** Ausente mantem o usuario atual; na primeira vez, o servidor deriva do slug. */
    usuario: z.string().min(3).max(80).optional(),
    /** Ausente usa a senha padrao do sistema, com troca obrigatoria no primeiro acesso. */
    senha: senhaPortalSchema.optional(),
});
/**
 * A resposta traz a senha em claro UMA vez, para o operador repassar.
 *
 * E o unico momento em que ela existe fora do hash. Nao volta em nenhuma
 * consulta depois disso — nem para o operador, nem para o proprio cliente.
 */
export const credencialEmitidaSchema = z.object({
    usuario: z.string(),
    senha: z.string(),
    precisaTrocarSenha: z.boolean(),
});
//# sourceMappingURL=auth.js.map